import {
  PRIMARY_SURVEY_ID_SYSTEM,
  QuestionnaireResponseAttachment,
} from '../Attachment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNotify } from '../../../../../../hooks';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Survey } from '../../../../../Surveys';
import _noop from 'lodash/noop';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { useModalStyles } from './styles';
import { defineMessages, useIntl } from 'react-intl';
import useFhirSearch, {
  Hits,
} from '../../../../../Surveys/util/FhirSearch/useFhirSearch';

const messages = defineMessages({
  surveyError: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.surveyViewModal.surveyError',
    defaultMessage: 'Error loading survey',
  },
});

type SurveyViewProps = {
  attachment: QuestionnaireResponseAttachment;
  onDismiss: () => void;
};

function parseQuestionnaireIdFromResponse(
  qr: fhir.QuestionnaireResponse | undefined,
): string | undefined {
  if (!qr?.questionnaire) {
    return undefined;
  }
  const ref =
    typeof qr.questionnaire === 'string'
      ? qr.questionnaire
      : qr.questionnaire.reference;
  if (!ref) {
    return undefined;
  }
  const path = ref.split('?')[0] ?? ref;
  const segments = path.split('/').filter(Boolean);
  const typeIdx = segments.lastIndexOf('Questionnaire');
  if (typeIdx >= 0 && typeIdx < segments.length - 1) {
    return segments[typeIdx + 1];
  }
  return segments.length >= 1 ? segments[segments.length - 1] : undefined;
}

type SearchPhase = 'questionnaire-response' | 'questionnaire';

export const SurveyViewModal = (props: SurveyViewProps) => {
  const { attachment, onDismiss } = props;
  const classes = useModalStyles();
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const [storedQuestionnaireResponse, setStoredQuestionnaireResponse] =
    useState<fhir.QuestionnaireResponse | null>(null);
  const [searchPhase, setSearchPhase] = useState<SearchPhase>(
    'questionnaire-response',
  );
  const surveyErrorReportedRef = useRef(false);

  useEffect(() => {
    setSearchPhase('questionnaire-response');
    setStoredQuestionnaireResponse(null);
    surveyErrorReportedRef.current = false;
  }, [attachment.resource.id]);

  const questionnaireResponseQuery = useMemo(
    () => ({
      type: 'select',
      columns: '*',
      from: [{ table: 'questionnaire' }, { table: 'questionnaire_response' }],
      limit: [
        { type: 'number', value: 0 },
        { type: 'number', value: 1 },
      ],
      where: {
        type: 'elasticsearch',
        query: {
          bool: {
            must: [
              {
                term: {
                  'resourceType.keyword': 'QuestionnaireResponse',
                },
              },
              {
                term: {
                  'id.keyword': attachment.resource.id,
                },
              },
            ],
          },
        },
      },
    }),
    [attachment.resource.id],
  );

  const questionnaireQuery = useMemo(() => {
    if (!storedQuestionnaireResponse) {
      return null;
    }
    const should: Record<string, unknown>[] = [];
    if (attachment.primarySurveyId) {
      should.push({
        bool: {
          must: [
            {
              term: {
                'resourceType.keyword': 'Questionnaire',
              },
            },
            {
              term: {
                'meta.tag.system.keyword': PRIMARY_SURVEY_ID_SYSTEM,
              },
            },
            {
              term: {
                'meta.tag.code.keyword': attachment.primarySurveyId,
              },
            },
          ],
        },
      });
    }
    const questionnaireId = parseQuestionnaireIdFromResponse(
      storedQuestionnaireResponse,
    );
    if (questionnaireId) {
      should.push({
        bool: {
          must: [
            {
              term: {
                'resourceType.keyword': 'Questionnaire',
              },
            },
            {
              term: {
                'id.keyword': questionnaireId,
              },
            },
          ],
        },
      });
    }
    if (should.length === 0) {
      return null;
    }
    return {
      type: 'select',
      columns: '*',
      from: [{ table: 'questionnaire' }, { table: 'questionnaire_response' }],
      limit: [
        { type: 'number', value: 0 },
        { type: 'number', value: 25 },
      ],
      where: {
        type: 'elasticsearch',
        query: {
          bool: {
            should,
          },
        },
      },
    };
  }, [storedQuestionnaireResponse, attachment.primarySurveyId]);

  const activeQuery =
    searchPhase === 'questionnaire-response'
      ? questionnaireResponseQuery
      : questionnaireQuery;

  const [response, isLoading] = useFhirSearch<
    Hits<fhir.QuestionnaireResponse | fhir.Questionnaire>
  >(activeQuery, {
    // We need to search the entire project to find the Questionnaire and QuestionnaireResponses
    // because Questionnaires are not tied to a specific patient (/patients/:id) or to a specific
    // cohort (/cohorts/:id).
    searchEntireProject: true,
  });

  // Must be useEffect (not useLayoutEffect): passive effects run after useFhirSearch's
  // useEffect starts the next fetch. Layout effects run first and would see stale phase-1
  // hits with isLoading still false, then incorrectly toast "Error loading survey".
  useEffect(() => {
    if (isLoading) {
      return;
    }
    const reportSurveyError = () => {
      if (surveyErrorReportedRef.current) {
        return;
      }
      surveyErrorReportedRef.current = true;
      notify(formatMessage(messages.surveyError), 'error');
      onDismiss();
    };
    if (searchPhase === 'questionnaire-response') {
      const hits = response?.hits?.hits ?? [];
      const questionnaireResponse = hits.find(
        hit => hit._source.resourceType === 'QuestionnaireResponse',
      )?._source as fhir.QuestionnaireResponse | undefined;
      if (!questionnaireResponse) {
        reportSurveyError();
        return;
      }
      const canResolveQuestionnaire =
        !!attachment.primarySurveyId ||
        !!parseQuestionnaireIdFromResponse(questionnaireResponse);
      if (!canResolveQuestionnaire) {
        reportSurveyError();
        return;
      }
      setStoredQuestionnaireResponse(questionnaireResponse);
      setSearchPhase('questionnaire');
      return;
    }
    const hits = response?.hits?.hits ?? [];
    const questionnaire = hits.find(
      hit => hit._source.resourceType === 'Questionnaire',
    )?._source as fhir.Questionnaire | undefined;
    const responseLooksLikeStalePhase1 =
      !questionnaire &&
      hits.some(hit => hit._source.resourceType === 'QuestionnaireResponse');
    if (responseLooksLikeStalePhase1) {
      return;
    }
    if (!questionnaire) {
      reportSurveyError();
    }
  }, [
    isLoading,
    response,
    searchPhase,
    attachment.primarySurveyId,
    notify,
    formatMessage,
    onDismiss,
  ]);

  const hits = response?.hits?.hits ?? [];
  const questionnaireFromSearch = hits.find(
    hit => hit._source.resourceType === 'Questionnaire',
  )?._source as fhir.Questionnaire | undefined;
  const questionnaire =
    searchPhase === 'questionnaire' ? questionnaireFromSearch : undefined;
  const questionnaireResponse =
    searchPhase === 'questionnaire' ? storedQuestionnaireResponse : undefined;

  return (
    <Modal
      isFullScreen
      isOpen={true}
      onDismiss={onDismiss}
      contentClassName={classes.modalContent}
      data-testid="survey-view-modal"
    >
      {isLoading ? (
        <Box fullWidth fullHeight justify="center" align="center">
          <DotLoader />
        </Box>
      ) : (
        <Box paddingTop={2}>
          {!!questionnaire && !!questionnaireResponse && (
            <Survey
              isReadOnly
              onComplete={_noop}
              onResponseUpdate={_noop}
              questionnaire={questionnaire}
              questionnaireResponse={questionnaireResponse}
            />
          )}
        </Box>
      )}
    </Modal>
  );
};
